import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import HalEye from '../components/common/HalEye'
import { 
  HiChat, 
  HiPlus, 
  HiPaperAirplane, 
  HiPaperClip,
  HiFolder,
  HiSearch,
  HiDotsVertical,
  HiTrash,
  HiClock,
  HiSparkles,
  HiX,
  HiDocumentText,
  HiPhotograph,
  HiDownload,
  HiRefresh,
  HiLightningBolt,
  HiClipboardList,
  HiTrendingUp,
  HiBell,
  HiUserGroup,
  HiTable,
  HiChevronDown,
  HiChevronUp,
  HiUpload,
  HiCalendar,
  HiGlobe
} from 'react-icons/hi'
import ReactMarkdown from 'react-markdown'
import DOMPurify from 'dompurify'
import { format } from 'date-fns'
import { projectsApi, aiApi, calendarApi } from '../api/client'
import { useAIChat } from '../hooks/useAIChat'

// SharePoint icon SVG component
function SharePointIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.5 2C8.36 2 5 5.36 5 9.5c0 .71.1 1.4.28 2.06A5.5 5.5 0 002 16.5C2 19.54 4.46 22 7.5 22h9c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96A7.49 7.49 0 0012.5 2zm0 2a5.5 5.5 0 014.9 7.97l-.3.53H16.5a3 3 0 013 3 3 3 0 01-3 3h-9A3.5 3.5 0 014 16.5a3.5 3.5 0 012.83-3.43l.83-.17-.17-.83A5.49 5.49 0 017 9.5C7 6.46 9.46 4 12.5 4z"/>
      <text x="9" y="16" fontSize="8" fontWeight="bold" fill="currentColor">S</text>
    </svg>
  )
}

// Supported file types for AI parsing
const SUPPORTED_FILE_TYPES = '.xlsx,.xls,.csv,.tsv,.docx,.txt,.md,.json'
const SUPPORTED_EXTENSIONS = new Set(['xlsx', 'xls', 'csv', 'tsv', 'docx', 'txt', 'md', 'json'])
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

// Quick prompts matching sidebar style
const QUICK_PROMPTS = [
  {
    icon: HiClipboardList,
    label: "What are my tasks?",
    prompt: "Show me all my current tasks, prioritized by due date. Highlight anything overdue.",
    color: "brand"
  },
  {
    icon: HiLightningBolt,
    label: "Create a task",
    prompt: "I need to create a new task. Help me set it up with the right project, priority, and due date.",
    color: "orange"
  },
  {
    icon: HiTrendingUp,
    label: "Project status",
    prompt: "Give me a quick status update on all my projects. Show progress, overdue items, and blockers.",
    color: "green"
  },
  {
    icon: HiBell,
    label: "Set a reminder",
    prompt: "Help me set up a reminder for an upcoming task or deadline.",
    color: "purple"
  },
  {
    icon: HiSearch,
    label: "Find tasks",
    prompt: "Help me search for a specific task across all projects.",
    color: "blue"
  },
  {
    icon: HiUserGroup,
    label: "Team update",
    prompt: "Show me what tasks my team is working on and who might be overloaded.",
    color: "teal"
  },
  {
    icon: HiTable,
    label: "Import from file",
    prompt: "I have a spreadsheet with project and task data I'd like to import. I'll attach the file — please parse it and create the projects and tasks for me.",
    color: "emerald"
  },
  {
    icon: HiGlobe,
    label: "Connect SharePoint",
    prompt: "Search my organization's SharePoint sites and show me what's available to import.",
    color: "teal"
  },
]

const colorMap = {
  brand: 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  green: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  teal: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
}

// Full capabilities list
const AI_CAPABILITIES = [
  {
    category: "Project Management",
    icon: HiDocumentText,
    actions: [
      "Create new projects with boards & columns",
      "Update project status, priority & details",
      "Get project summaries with progress stats",
      "Add or list team members on projects",
    ]
  },
  {
    category: "Task Management",
    icon: HiClipboardList,
    actions: [
      "Create, update, and delete tasks",
      "Change task status, priority & due dates",
      "Move tasks between board columns",
      "Add notes & comments to tasks",
      "Search tasks by keyword, status, or assignee",
    ]
  },
  {
    category: "Reminders & Notifications",
    icon: HiBell,
    actions: [
      "Set one-time, daily, or weekly reminders",
      "Send email or Teams notifications",
      "List your pending reminders",
      "Get alerted about overdue tasks",
    ]
  },
  {
    category: "Insights & Reporting",
    icon: HiTrendingUp,
    actions: [
      "View overdue tasks across all projects",
      "Get your personal task dashboard",
      "Track project progress & completion rates",
      "Process meeting notes into action items",
    ]
  },
  {
    category: "SharePoint Integration",
    icon: HiGlobe,
    actions: [
      "Search your organization's SharePoint sites",
      "Browse and import SharePoint lists",
      "Import documents from SharePoint",
      "Map SharePoint list columns to project tasks",
    ]
  },
  {
    category: "Calendar & Exports",
    icon: HiCalendar,
    actions: [
      "Export your tasks as .ics calendar file",
      "Export project tasks to your calendar",
      "Export reminders to Outlook, Google, or Apple Calendar",
    ]
  },
]

export default function AIChatPage() {
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [loadingConversations, setLoadingConversations] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [attachedFiles, setAttachedFiles] = useState([])
  const [showAllCapabilities, setShowAllCapabilities] = useState(false)
  const [showSharePointPanel, setShowSharePointPanel] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [fileError, setFileError] = useState(null)
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const [multiSelectMode, setMultiSelectMode] = useState(false)
  const [selectedConvIds, setSelectedConvIds] = useState(new Set())
  const optionsMenuRef = useRef(null)
  
  const {
    connected,
    conversationId,
    messages,
    isTyping,
    toolExecutions,
    statusMessage,
    error,
    uploadingFiles,
    newConversation,
    sendMessage,
    loadConversation,
    clearError,
    connect,
    disconnect,
  } = useAIChat()

  const [input, setInput] = useState('')
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const fileInputRef = useRef(null)

  // Close options menu on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(e.target)) {
        setShowOptionsMenu(false)
      }
    }
    if (showOptionsMenu) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showOptionsMenu])

  const handleCalendarExport = async (type) => {
    setShowOptionsMenu(false)
    try {
      let response
      let filename
      if (type === 'my-tasks') {
        response = await calendarApi.downloadMyTasks()
        filename = 'my-tasks.ics'
      } else if (type === 'project' && selectedProject) {
        response = await calendarApi.downloadProject(selectedProject.id)
        filename = `${selectedProject.name.replace(/\s+/g, '_')}-tasks.ics`
      } else if (type === 'reminders') {
        response = await calendarApi.downloadReminders()
        filename = 'reminders.ics'
      }
      if (response) {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        window.URL.revokeObjectURL(url)
      }
    } catch (err) {
      console.error('Calendar export failed:', err)
    }
  }

  // Connect WebSocket on mount
  useEffect(() => {
    connect()
    return () => disconnect()
  }, [connect, disconnect])

  // Auto-start a new conversation when connected but no active conversation
  useEffect(() => {
    if (connected && !conversationId) {
      newConversation(selectedProject?.id)
    }
  }, [connected, conversationId, newConversation, selectedProject?.id])

  // When the user switches projects, start a fresh conversation
  // so it gets tagged with the correct project_id.
  const prevProjectRef = useRef(undefined)   // undefined = initial render
  useEffect(() => {
    // Skip the very first render (initial mount) — the auto-start
    // effect already handles that.
    if (prevProjectRef.current === undefined) {
      prevProjectRef.current = selectedProject?.id ?? null
      return
    }
    const newProjId = selectedProject?.id ?? null
    if (newProjId !== prevProjectRef.current) {
      prevProjectRef.current = newProjId
      // Only create a new conversation if we're connected
      if (connected) {
        setSelectedConversation(null)
        newConversation(newProjId)
      }
    }
  }, [selectedProject?.id, connected, newConversation])

  // Fetch only user's own projects (not all org projects)
  useEffect(() => {
    async function fetchProjects() {
      try {
        const response = await projectsApi.listMyProjects()
        setProjects(response.data)
      } catch (error) {
        console.error('Failed to fetch projects:', error)
      } finally {
        setLoadingProjects(false)
      }
    }
    fetchProjects()
  }, [])

  // Reusable function to refresh conversation list
  const fetchConversations = useCallback(async () => {
    try {
      const response = await aiApi.listConversations(selectedProject?.id)
      setConversations(response.data)
    } catch (error) {
      console.error('Failed to fetch conversations:', error)
    }
  }, [selectedProject?.id])

  // Fetch conversations for selected project
  useEffect(() => {
    setLoadingConversations(true)
    fetchConversations().finally(() => setLoadingConversations(false))
  }, [fetchConversations])

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping, toolExecutions])

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv)
    loadConversation(conv.id)
  }

  const handleNewChat = () => {
    setSelectedConversation(null)
    newConversation(selectedProject?.id)
    setShowAllCapabilities(false)
    setAttachedFiles([])
  }

  const handleDeleteConversation = async (conv) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return
    try {
      await aiApi.deleteConversation(conv.id)
      setConversations(prev => prev.filter(c => c.id !== conv.id))
      if (selectedConversation?.id === conv.id || conversationId === conv.id) {
        setSelectedConversation(null)
        newConversation(selectedProject?.id)
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error)
      alert('Failed to delete conversation')
    }
  }

  const toggleConvSelection = (convId) => {
    setSelectedConvIds(prev => {
      const next = new Set(prev)
      if (next.has(convId)) next.delete(convId)
      else next.add(convId)
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedConvIds.size === filteredConversations.length) {
      setSelectedConvIds(new Set())
    } else {
      setSelectedConvIds(new Set(filteredConversations.map(c => c.id)))
    }
  }

  const handleBulkDelete = async () => {
    if (selectedConvIds.size === 0) return
    if (!window.confirm(`Delete ${selectedConvIds.size} conversation${selectedConvIds.size > 1 ? 's' : ''}?`)) return
    try {
      await aiApi.bulkDeleteConversations([...selectedConvIds])
      setConversations(prev => prev.filter(c => !selectedConvIds.has(c.id)))
      if (selectedConvIds.has(selectedConversation?.id) || selectedConvIds.has(conversationId)) {
        setSelectedConversation(null)
        newConversation(selectedProject?.id)
      }
      setSelectedConvIds(new Set())
      setMultiSelectMode(false)
    } catch (error) {
      console.error('Failed to bulk delete conversations:', error)
      alert('Failed to delete conversations')
    }
  }

  const exitMultiSelect = () => {
    setMultiSelectMode(false)
    setSelectedConvIds(new Set())
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if ((!input.trim() && attachedFiles.length === 0) || !connected || uploadingFiles) return
    
    // Must have a conversation before sending
    if (!conversationId) return
    
    const message = input.trim() || (attachedFiles.length > 0 
      ? `Please analyze the attached file${attachedFiles.length > 1 ? 's' : ''} and help me create projects and tasks from the content.`
      : '')
    
    sendMessage(message, selectedProject?.id, attachedFiles)
    setInput('')
    setAttachedFiles([])
    
    // Refresh conversation list after a short delay so auto-title shows up
    setTimeout(() => fetchConversations(), 2000)
  }

  const handleQuickPrompt = (prompt) => {
    if (!connected || !conversationId) return
    sendMessage(prompt, selectedProject?.id)
    setShowAllCapabilities(false)
  }

  const validateAndAddFiles = (fileList) => {
    const valid = []
    for (const file of fileList) {
      const ext = file.name.split('.').pop()?.toLowerCase()
      if (!SUPPORTED_EXTENSIONS.has(ext)) {
        setFileError(`Unsupported file type: .${ext}. Supported: Excel, Word, CSV, JSON, text`)
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`${file.name} is too large. Maximum 10MB.`)
        continue
      }
      valid.push(file)
    }
    if (valid.length > 0) {
      setAttachedFiles(prev => [...prev, ...valid])
    }
  }

  const handleFileSelect = (e) => {
    if (e.target.files?.length > 0) {
      validateAndAddFiles(Array.from(e.target.files))
    }
    e.target.value = ''
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    if (e.dataTransfer.files?.length > 0) {
      validateAndAddFiles(Array.from(e.dataTransfer.files))
    }
  }

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const filteredConversations = conversations.filter(conv => 
    !searchQuery || 
    conv.title?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="h-full flex bg-surface-50 dark:bg-surface-900">
      {/* Projects Sidebar */}
      <aside className="w-64 bg-white dark:bg-surface-800 border-r border-surface-200 dark:border-surface-700 flex flex-col shrink-0">
        <div className="p-4 border-b border-surface-200 dark:border-surface-700">
          <h2 className="font-semibold text-surface-800 dark:text-surface-200 flex items-center gap-2">
            <HiFolder className="w-5 h-5 text-brand-500" />
            My Projects
          </h2>
        </div>

        {/* SharePoint Connect - prominent position */}
        <div className="px-2 pt-2">
          <button
            onClick={() => setShowSharePointPanel(!showSharePointPanel)}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
              showSharePointPanel
                ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 font-medium border border-teal-200 dark:border-teal-700'
                : 'text-surface-600 dark:text-surface-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:text-teal-700 dark:hover:text-teal-300 border border-surface-200 dark:border-surface-600 hover:border-teal-200 dark:hover:border-teal-700'
            }`}
          >
            <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
              showSharePointPanel 
                ? 'bg-teal-100 dark:bg-teal-800' 
                : 'bg-surface-100 dark:bg-surface-700'
            }`}>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="5" className={showSharePointPanel ? 'fill-teal-500' : 'fill-teal-400'} opacity="0.9"/>
                <circle cx="7" cy="14" r="4" className={showSharePointPanel ? 'fill-teal-400' : 'fill-teal-300'} opacity="0.8"/>
                <circle cx="15" cy="15" r="3.5" className={showSharePointPanel ? 'fill-teal-300' : 'fill-teal-200'} opacity="0.7"/>
              </svg>
            </div>
            <div className="text-left min-w-0">
              <span className="font-medium text-xs block">SharePoint</span>
              <span className="text-[10px] text-surface-400 dark:text-surface-500 block">Connect & import data</span>
            </div>
          </button>
        </div>
        
        <nav className="flex-1 overflow-y-auto p-2">
          {/* All Projects Option */}
          <button
            onClick={() => setSelectedProject(null)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
              selectedProject === null
                ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-medium'
                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700'
            }`}
          >
            <span className="w-3 h-3 rounded-full bg-gradient-to-br from-brand-400 to-purple-500" />
            All Projects
          </button>

          {loadingProjects ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="mt-2 space-y-1">
              {projects.map(project => (
                <button
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    selectedProject?.id === project.id
                      ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-medium'
                      : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700'
                  }`}
                >
                  <span 
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: project.color || '#3b82f6' }}
                  />
                  <span className="truncate">{project.name}</span>
                </button>
              ))}
            </div>
          )}
        </nav>
      </aside>

      {/* SharePoint Panel (conditionally shown) */}
      {showSharePointPanel && (
        <aside className="w-72 bg-white dark:bg-surface-800 border-r border-surface-200 dark:border-surface-700 flex flex-col shrink-0">
          <div className="p-4 border-b border-surface-200 dark:border-surface-700">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-surface-800 dark:text-surface-200 flex items-center gap-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="5" className="fill-teal-500" opacity="0.9"/>
                  <circle cx="7" cy="14" r="4" className="fill-teal-400" opacity="0.8"/>
                  <circle cx="15" cy="15" r="3.5" className="fill-teal-300" opacity="0.7"/>
                </svg>
                SharePoint
              </h2>
              <button
                onClick={() => setShowSharePointPanel(false)}
                className="p-1.5 text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg transition-colors"
              >
                <HiX className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="text-center py-4">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-100 to-blue-100 dark:from-teal-900/40 dark:to-blue-900/40 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-sm">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="5" className="fill-teal-500" opacity="0.9"/>
                  <circle cx="7" cy="14" r="4" className="fill-teal-400" opacity="0.8"/>
                  <circle cx="15" cy="15" r="3.5" className="fill-teal-300" opacity="0.7"/>
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-surface-800 dark:text-surface-200 mb-1">
                SharePoint Integration
              </h3>
              <p className="text-xs text-surface-500 leading-relaxed">
                Connect to your organization's SharePoint to import lists, documents, and data into your projects.
              </p>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => handleQuickPrompt("Search my organization's SharePoint sites and show me what's available.")}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-700/50 hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-sm transition-all text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 flex items-center justify-center shrink-0">
                  <HiSearch className="w-4 h-4" />
                </div>
                <span className="text-sm text-surface-700 dark:text-surface-300 font-medium group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                  Browse SharePoint sites
                </span>
              </button>

              <button
                onClick={() => handleQuickPrompt("Import data from a SharePoint list into a project. Help me find the right list and map the columns.")}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-700/50 hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-sm transition-all text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex items-center justify-center shrink-0">
                  <HiDownload className="w-4 h-4" />
                </div>
                <span className="text-sm text-surface-700 dark:text-surface-300 font-medium group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                  Import from SharePoint list
                </span>
              </button>

              <button
                onClick={() => handleQuickPrompt("Find and import a document from SharePoint that I can use as a reference for my project tasks.")}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-700/50 hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-sm transition-all text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center shrink-0">
                  <HiDocumentText className="w-4 h-4" />
                </div>
                <span className="text-sm text-surface-700 dark:text-surface-300 font-medium group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                  Import SharePoint document
                </span>
              </button>
            </div>

            <div className="mt-4 p-3 bg-surface-50 dark:bg-surface-700/50 rounded-xl border border-surface-200 dark:border-surface-600">
              <p className="text-xs text-surface-500 dark:text-surface-400 leading-relaxed">
                <strong className="text-surface-700 dark:text-surface-300">Tip:</strong> You can also ask the AI to search SharePoint directly in the chat. Try: "Find the Q1 report on SharePoint" or "Import the marketing tasks list from SharePoint."
              </p>
            </div>
          </div>
        </aside>
      )}

      {/* Conversations List */}
      <aside className="w-72 bg-white dark:bg-surface-800 border-r border-surface-200 dark:border-surface-700 flex flex-col shrink-0">
        <div className="p-4 border-b border-surface-200 dark:border-surface-700">
          {multiSelectMode ? (
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSelectAll}
                  className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                >
                  {selectedConvIds.size === filteredConversations.length ? 'Deselect all' : 'Select all'}
                </button>
                <span className="text-xs text-surface-500">
                  {selectedConvIds.size} selected
                </span>
              </div>
              <div className="flex items-center gap-1">
                {selectedConvIds.size > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 rounded-lg transition-all"
                    title="Delete selected"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={exitMultiSelect}
                  className="p-2 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg transition-all"
                  title="Cancel"
                >
                  <HiX className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-surface-800 dark:text-surface-200 flex items-center gap-2">
                <HiChat className="w-5 h-5 text-brand-500" />
                Conversations
              </h2>
              <div className="flex items-center gap-1">
                {filteredConversations.length > 0 && (
                  <button
                    onClick={() => setMultiSelectMode(true)}
                    className="p-2 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg transition-all"
                    title="Select conversations"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={handleNewChat}
                  className="p-2 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg transition-all"
                  title="New conversation"
                >
                  <HiRefresh className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          
          {/* Search */}
          <div className="relative">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-surface-200 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-700 text-surface-800 dark:text-surface-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 placeholder-surface-400"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loadingConversations ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-8 px-4">
              <HiSparkles className="w-10 h-10 text-surface-300 dark:text-surface-600 mx-auto mb-2" />
              <p className="text-sm text-surface-500">No conversations yet</p>
              <button
                onClick={handleNewChat}
                className="mt-3 text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium"
              >
                Start a new chat
              </button>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {filteredConversations.map(conv => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  isSelected={selectedConversation?.id === conv.id || conversationId === conv.id}
                  onClick={() => multiSelectMode ? toggleConvSelection(conv.id) : handleSelectConversation(conv)}
                  onDelete={() => handleDeleteConversation(conv)}
                  multiSelectMode={multiSelectMode}
                  isChecked={selectedConvIds.has(conv.id)}
                  onToggleCheck={() => toggleConvSelection(conv.id)}
                />
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Main Chat Area */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <header className="h-14 border-b border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 flex items-center justify-between px-6 shrink-0">
          <div className="flex items-center gap-3">
            <HalEye size="w-9 h-9" />
            <div>
              <h1 className="font-semibold text-surface-800 dark:text-surface-200">HAL 9000</h1>
              <p className="text-xs text-surface-500">
                {selectedProject ? (
                  <span className="flex items-center gap-1">
                    <span 
                      className="w-2 h-2 rounded-full inline-block" 
                      style={{ backgroundColor: selectedProject.color || '#3b82f6' }}
                    />
                    {selectedProject.name}
                  </span>
                ) : 'General conversation'}
                {connected && <span className="ml-2"><span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block" /> Connected</span>}
                {!connected && <span className="ml-2"><span className="w-1.5 h-1.5 bg-red-500 rounded-full inline-block" /> Disconnected</span>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={handleNewChat}
              className="p-2 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg transition-all"
              title="New conversation"
            >
              <HiRefresh className="w-4 h-4" />
            </button>
            <div className="relative" ref={optionsMenuRef}>
              <button
                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                className="p-2 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg transition-all"
                title="Options"
              >
                <HiDotsVertical className="w-4 h-4" />
              </button>
              {showOptionsMenu && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl shadow-lg z-50 py-1">
                  <p className="px-3 py-1.5 text-xs font-semibold text-surface-400 dark:text-surface-500 uppercase tracking-wide">Calendar Sync</p>
                  <button onClick={() => handleCalendarExport('my-tasks')} className="w-full text-left px-3 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-600 flex items-center gap-2">
                    <HiCalendar className="w-4 h-4 text-brand-500" />
                    Export My Tasks (.ics)
                  </button>
                  {selectedProject && (
                    <button onClick={() => handleCalendarExport('project')} className="w-full text-left px-3 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-600 flex items-center gap-2">
                      <HiCalendar className="w-4 h-4 text-green-500" />
                      Export Project Tasks (.ics)
                    </button>
                  )}
                  <button onClick={() => handleCalendarExport('reminders')} className="w-full text-left px-3 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-600 flex items-center gap-2">
                    <HiBell className="w-4 h-4 text-purple-500" />
                    Export Reminders (.ics)
                  </button>
                  <div className="border-t border-surface-200 dark:border-surface-600 my-1" />
                  <button
                    onClick={() => { setShowOptionsMenu(false); setShowSharePointPanel(!showSharePointPanel) }}
                    className="w-full text-left px-3 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-600 flex items-center gap-2"
                  >
                    <HiGlobe className="w-4 h-4 text-teal-500" />
                    {showSharePointPanel ? 'Hide' : 'Show'} SharePoint Panel
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Project Context Banner */}
        {selectedProject && (
          <div className="px-6 py-2.5 bg-gradient-to-r from-brand-50 to-blue-50 dark:from-brand-900/30 dark:to-blue-900/30 border-b border-brand-100 dark:border-brand-800 flex items-center gap-2">
            <span 
              className="w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-white dark:ring-surface-800 shadow-sm"
              style={{ backgroundColor: selectedProject.color || '#3b82f6' }}
            />
            <span className="text-xs font-medium text-brand-700 dark:text-brand-300 truncate">
              Context: {selectedProject.name}
            </span>
          </div>
        )}

        {/* Messages Area */}
        <div 
          className={`flex-1 overflow-y-auto p-6 transition-colors ${isDragOver ? 'bg-brand-50/50 dark:bg-brand-900/10 ring-2 ring-inset ring-brand-300 dark:ring-brand-700 ring-dashed' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drag-and-drop overlay */}
          {isDragOver && (
            <div className="flex flex-col items-center justify-center py-8 pointer-events-none">
              <HiUpload className="w-10 h-10 text-brand-400 mb-2" />
              <p className="text-sm font-medium text-brand-600 dark:text-brand-400">Drop files here</p>
              <p className="text-xs text-brand-500">Excel, Word, CSV, JSON, or text</p>
            </div>
          )}

          <div className="max-w-3xl mx-auto space-y-4">
            {/* Welcome / Quick prompts (matching sidebar style) */}
            {messages.length === 0 && !isTyping && !showAllCapabilities && (
              <div className="space-y-5">
                <div className="text-center pt-8 pb-2">
                  <HalEye size="w-16 h-16" className="mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-surface-800 dark:text-surface-200 mb-2">
                    HAL 9000
                  </h2>
                  <p className="text-surface-500 max-w-md mx-auto text-sm leading-relaxed">
                    Good morning. I'm HAL 9000, your project manager. I keep things moving so you don't have to. Try one of these:
                    {selectedProject && (
                      <span className="block mt-2 text-brand-600 dark:text-brand-400">
                        Currently focused on: <strong>{selectedProject.name}</strong>
                      </span>
                    )}
                  </p>
                </div>

                {/* Quick prompt buttons (styled like sidebar) */}
                <div className="max-w-lg mx-auto space-y-2">
                  {QUICK_PROMPTS.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickPrompt(item.prompt)}
                      disabled={!connected}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-700/50 hover:border-brand-300 dark:hover:border-brand-600 hover:shadow-sm transition-all duration-150 text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${colorMap[item.color]}`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      <span className="text-sm text-surface-700 dark:text-surface-300 font-medium group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>

                {/* See all capabilities toggle */}
                <div className="text-center">
                  <button
                    onClick={() => setShowAllCapabilities(true)}
                    className="inline-flex items-center gap-1 py-2 text-xs font-medium text-ai-600 dark:text-ai-400 hover:text-ai-700 dark:hover:text-ai-300 transition-colors"
                  >
                    See everything I can do
                    <HiChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Full capabilities panel */}
            {messages.length === 0 && showAllCapabilities && (
              <div className="max-w-lg mx-auto space-y-4">
                <button
                  onClick={() => setShowAllCapabilities(false)}
                  className="flex items-center gap-1 text-xs font-medium text-ai-600 dark:text-ai-400 hover:text-ai-700 dark:hover:text-ai-300 transition-colors"
                >
                  <HiChevronUp className="w-3.5 h-3.5" />
                  Back to quick actions
                </button>
                
                <div className="text-center pb-1">
                  <h3 className="text-sm font-semibold text-surface-800 dark:text-surface-200">
                    HAL 9000 Capabilities
                  </h3>
                  <p className="text-xs text-surface-500 mt-0.5">
                    Everything HAL 9000 can do
                  </p>
                </div>

                {AI_CAPABILITIES.map((cap, idx) => (
                  <div key={idx} className="bg-surface-50 dark:bg-surface-700/50 rounded-xl p-4 border border-surface-200 dark:border-surface-600">
                    <div className="flex items-center gap-2 mb-2">
                      <cap.icon className="w-4 h-4 text-ai-600 dark:text-ai-400" />
                      <h4 className="text-xs font-semibold text-surface-800 dark:text-surface-200 uppercase tracking-wide">
                        {cap.category}
                      </h4>
                    </div>
                    <ul className="space-y-1.5">
                      {cap.actions.map((action, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-surface-600 dark:text-surface-400">
                          <span className="w-1 h-1 bg-ai-400 rounded-full mt-1.5 shrink-0"></span>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {/* Quick prompts at bottom */}
                <div className="space-y-1.5 pt-1">
                  <p className="text-xs font-medium text-surface-500 px-1">Try asking me:</p>
                  {QUICK_PROMPTS.slice(0, 3).map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickPrompt(item.prompt)}
                      disabled={!connected}
                      className="w-full text-left px-3 py-2 rounded-lg text-xs text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-600 transition-colors disabled:opacity-50"
                    >
                      &ldquo;{item.label}&rdquo;
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.map((message, idx) => (
              <ChatMessage key={message.id || idx} message={message} />
            ))}

            {/* Tool Executions */}
            {toolExecutions.length > 0 && (
              <ToolExecutionIndicator executions={toolExecutions} />
            )}

            {/* Activity indicator */}
            {isTyping && (
              <div className="flex items-start gap-3 animate-fade-in">
                <div className="shrink-0 mt-0.5">
                  <div className="w-8 h-8 rounded-full bg-surface-900 dark:bg-black flex items-center justify-center" style={{ boxShadow: '0 0 12px 2px rgba(124, 58, 237, 0.35)' }}>
                    <svg viewBox="0 0 24 24" className="w-4 h-4 text-purple-400 animate-spin" style={{ animationDuration: '1.5s' }}>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" />
                      <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 min-w-0">
                  {statusMessage ? (
                    <div
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border animate-fade-in"
                      style={{
                        background: 'linear-gradient(90deg, rgba(124,58,237,0.08) 0%, rgba(168,85,247,0.14) 50%, rgba(124,58,237,0.08) 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s ease-in-out infinite, fadeIn 0.15s ease-out',
                        borderColor: 'rgba(124,58,237,0.2)',
                        color: 'rgb(109, 40, 217)',
                      }}
                    >
                      <span>{statusMessage}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-surface-500 py-1.5">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-ai-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-ai-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-ai-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-sm text-surface-400">Thinking...</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4 text-red-700 dark:text-red-300 text-sm flex items-center justify-between">
                <span>{error}</span>
                <button onClick={clearError} className="text-red-500 hover:text-red-700">
                  <HiX className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* File error */}
            {fileError && (
              <div className="bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-sm rounded-lg p-3 flex items-center justify-between">
                <span>{fileError}</span>
                <button onClick={() => setFileError(null)} className="text-amber-500 hover:text-amber-700">
                  <HiX className="w-4 h-4" />
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="border-t border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800">
          {/* Staged file chips */}
          {attachedFiles.length > 0 && (
            <div className="px-6 pt-3 flex flex-wrap gap-1.5 max-w-3xl mx-auto">
              {attachedFiles.map((file, idx) => {
                const ext = file.name.split('.').pop()?.toLowerCase()
                const isSpreadsheet = ['xlsx', 'xls', 'csv', 'tsv'].includes(ext)
                return (
                  <span key={idx} className={`inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-lg text-xs font-medium border ${
                    isSpreadsheet 
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700'
                      : 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700'
                  }`}>
                    {isSpreadsheet ? <HiTable className="w-3 h-3" /> : <HiDocumentText className="w-3 h-3" />}
                    <span className="max-w-[150px] truncate">{file.name}</span>
                    <span className="text-surface-400 text-xs">({formatFileSize(file.size)})</span>
                    <button onClick={() => removeFile(idx)} className="p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10">
                      <HiX className="w-3 h-3" />
                    </button>
                  </span>
                )
              })}
            </div>
          )}

          {/* Upload progress indicator */}
          {uploadingFiles && (
            <div className="px-6 pt-2 max-w-3xl mx-auto">
              <div className="flex items-center gap-2 text-xs text-ai-600 dark:text-ai-400">
                <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Parsing file{attachedFiles.length > 1 ? 's' : ''}...
              </div>
            </div>
          )}

          <div className="p-4 max-w-3xl mx-auto">
            <form onSubmit={handleSubmit} className="flex items-end gap-2">
              {/* Attachment Button */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept={SUPPORTED_FILE_TYPES}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFiles || !connected}
                className="p-2.5 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg transition-all disabled:opacity-50 shrink-0"
                title="Attach file (Excel, Word, CSV...)"
              >
                <HiPaperClip className="w-5 h-5" />
              </button>

              {/* Input Field */}
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSubmit(e)
                    }
                  }}
                  placeholder={connected ? (attachedFiles.length > 0 ? 'Add instructions for this file...' : 'Ask me anything about your projects...') : 'Connecting...'}
                  rows={1}
                  disabled={uploadingFiles || !connected}
                  className="w-full px-4 py-3 border border-surface-200 dark:border-surface-600 rounded-xl resize-none bg-white dark:bg-surface-700 text-surface-800 dark:text-surface-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 max-h-32 disabled:bg-surface-50 dark:disabled:bg-surface-800 placeholder-surface-400"
                  style={{ minHeight: '48px' }}
                />
              </div>

              {/* Send Button */}
              <button
                type="submit"
                disabled={(!input.trim() && attachedFiles.length === 0) || !connected || uploadingFiles}
                className="p-3 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                {uploadingFiles ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <HiPaperAirplane className="w-5 h-5 rotate-90" />
                )}
              </button>
            </form>

            <p className="text-[10px] text-surface-400 dark:text-surface-500 text-center mt-2">
              Drop or attach Excel, Word, CSV, or JSON files to import projects & tasks. AI can make mistakes — please verify important information.
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}

function ConversationItem({ conversation, isSelected, onClick, onDelete, multiSelectMode, isChecked, onToggleCheck }) {
  const [showMenu, setShowMenu] = useState(false)

  const handleDelete = (e) => {
    e.stopPropagation()
    setShowMenu(false)
    onDelete()
  }

  return (
    <div className="relative group">
      <button
        onClick={onClick}
        className={`w-full text-left px-3 py-3 rounded-lg transition-all ${
          isChecked
            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            : isSelected
              ? 'bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-700'
              : 'hover:bg-surface-100 dark:hover:bg-surface-700 border border-transparent'
        }`}
      >
        <div className="flex items-start gap-3">
          {multiSelectMode ? (
            <div
              onClick={(e) => { e.stopPropagation(); onToggleCheck(); }}
              className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center shrink-0 cursor-pointer transition-colors ${
                isChecked
                  ? 'bg-red-500 border-red-500 text-white'
                  : 'border-surface-300 dark:border-surface-500 hover:border-red-400'
              }`}
            >
              {isChecked && (
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          ) : (
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
              isSelected ? 'bg-brand-100 dark:bg-brand-800' : 'bg-surface-100 dark:bg-surface-700'
            }`}>
              <HiChat className={`w-4 h-4 ${isSelected ? 'text-brand-600 dark:text-brand-400' : 'text-surface-500 dark:text-surface-400'}`} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-medium truncate ${
              isChecked
                ? 'text-red-700 dark:text-red-300'
                : isSelected ? 'text-brand-700 dark:text-brand-300' : 'text-surface-800 dark:text-surface-200'
            }`}>
              {conversation.title || 'New conversation'}
            </p>
            <p className="text-xs text-surface-500 flex items-center gap-1 mt-0.5">
              <HiClock className="w-3 h-3" />
              {format(new Date(conversation.updated_at || conversation.created_at), 'MMM d, h:mm a')}
            </p>
          </div>
        </div>
      </button>

      {/* Menu Button - hidden in multi-select mode */}
      {!multiSelectMode && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            setShowMenu(!showMenu)
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <HiDotsVertical className="w-4 h-4" />
        </button>
      )}

      {/* Dropdown Menu */}
      {showMenu && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-lg shadow-lg py-1 w-36">
            <button 
              onClick={handleDelete}
              className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
            >
              <HiTrash className="w-4 h-4" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function ChatMessage({ message }) {
  const isUser = message.role === 'user'

  // Detect bare HTML blocks that Claude forgot to wrap in ```hal-html fences.
  // If the content has substantial HTML (e.g. <div style= or <table) but no
  // hal-html fence, we auto-wrap for proper rendering.
  // Repair truncated HTML — close any unclosed tags so DOMPurify can render it
  const repairTruncatedHtml = (html) => {
    // If it ends mid-attribute or mid-tag, chop back to the last complete tag
    const lastCloseAngle = html.lastIndexOf('>')
    const lastOpenAngle = html.lastIndexOf('<')
    if (lastOpenAngle > lastCloseAngle) {
      // There's an unclosed '<' at the end — remove the partial tag
      html = html.slice(0, lastOpenAngle).trimEnd()
    }
    // Let DOMPurify handle any remaining unclosed elements — it auto-closes them
    return html
  }

  const ensureHtmlFenced = (content) => {
    if (!content) return content
    // Already has a hal-html fence — leave it alone
    if (/```hal-html/i.test(content)) return content
    // Check for substantial HTML structure (styled divs, tables, sections)
    const hasHtmlBlock = /<(?:div|table|section|article|header|nav)\b[^>]*style\s*=/i.test(content)
      || /<(?:table|thead|tbody)\b/i.test(content)
    if (!hasHtmlBlock) return content
    // Find the first opening tag and last closing tag to isolate the HTML block
    const firstTag = content.search(/<(?:div|table|section|article|header|nav)\b/i)
    if (firstTag === -1) return content
    const before = content.slice(0, firstTag).trim()
    let htmlPart = content.slice(firstTag).trim()
    // Repair any truncation damage before fencing
    htmlPart = repairTruncatedHtml(htmlPart)
    const parts = []
    if (before) parts.push(before + '\n\n')
    parts.push('```hal-html\n' + htmlPart + '\n```')
    return parts.join('')
  }

  // Parse assistant messages for ```hal-html blocks
  const renderAssistantContent = (content) => {
    if (!content) return null
    const fencedContent = ensureHtmlFenced(content)
    // Split on ```hal-html ... ``` fences (tolerant of \r\n, trailing spaces, etc.)
    const parts = fencedContent.split(/(```hal-html\s*\r?\n[\s\S]*?```)/g)
    return parts.map((part, i) => {
      const htmlMatch = part.match(/^```hal-html\s*\r?\n([\s\S]*?)```\s*$/)
      if (htmlMatch) {
        const cleanHtml = DOMPurify.sanitize(htmlMatch[1], {
          ALLOWED_TAGS: ['div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'table', 'thead', 'tbody', 'tr', 'th', 'td', 'ul', 'ol', 'li',
            'strong', 'em', 'b', 'i', 'br', 'hr', 'a', 'img', 'svg', 'path',
            'circle', 'rect', 'line', 'polyline', 'polygon', 'text', 'g',
            'section', 'header', 'footer', 'nav', 'main', 'article', 'aside',
            'figure', 'figcaption', 'details', 'summary', 'mark', 'small', 'sub', 'sup'],
          ALLOWED_ATTR: ['style', 'class', 'href', 'target', 'rel', 'src', 'alt',
            'width', 'height', 'viewBox', 'fill', 'stroke', 'stroke-width',
            'd', 'cx', 'cy', 'r', 'x', 'y', 'rx', 'ry', 'transform',
            'colspan', 'rowspan', 'role', 'aria-label', 'title'],
          FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
          FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur'],
        })
        return (
          <div
            key={i}
            className="hal-presentation my-2 rounded-xl overflow-hidden border border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-800"
            dangerouslySetInnerHTML={{ __html: cleanHtml }}
          />
        )
      }
      // Regular markdown text
      if (part.trim()) {
        return (
          <ReactMarkdown key={i}>{part}</ReactMarkdown>
        )
      }
      return null
    })
  }
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in`}>
      {!isUser && (
        <div className="shrink-0 mr-2.5 mt-1">
          <HalEye size="w-7 h-7" />
        </div>
      )}
      <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
        {/* File badges */}
        {isUser && message.files && message.files.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5 justify-end">
            {message.files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                <HiTable className="w-3 h-3" />
                {f.filename}
                {f.row_count != null && <span className="opacity-70">({f.row_count} rows)</span>}
              </span>
            ))}
          </div>
        )}
        <div className={`rounded-2xl px-4 py-3 shadow-sm ${
          isUser 
            ? 'bg-brand-600 text-white' 
            : 'bg-white dark:bg-surface-800 text-surface-800 dark:text-surface-200 border border-surface-200 dark:border-surface-700'
        }`}>
          {isUser ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="prose prose-sm max-w-none prose-headings:text-surface-800 dark:prose-headings:text-surface-200 prose-p:text-surface-700 dark:prose-p:text-surface-300 prose-a:text-brand-600 dark:prose-a:text-brand-400 prose-code:text-brand-600 dark:prose-code:text-brand-400 prose-code:bg-brand-50 dark:prose-code:bg-brand-900/30 prose-code:px-1 prose-code:rounded">
              {renderAssistantContent(message.content)}
            </div>
          )}
        </div>

        {/* Attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div className={`mt-2 flex flex-wrap gap-2 ${isUser ? 'justify-end' : ''}`}>
            {message.attachments.map((attachment, idx) => (
              <a
                key={idx}
                href={attachment.download_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isUser
                    ? 'bg-brand-500 text-brand-100 hover:bg-brand-400'
                    : 'bg-surface-100 dark:bg-surface-700 text-surface-700 dark:text-surface-300 hover:bg-surface-200 dark:hover:bg-surface-600'
                }`}
              >
                {attachment.file_type?.startsWith('image/') ? (
                  <HiPhotograph className="w-4 h-4" />
                ) : (
                  <HiDocumentText className="w-4 h-4" />
                )}
                <span className="max-w-[150px] truncate">{attachment.filename}</span>
                <HiDownload className="w-4 h-4 opacity-70" />
              </a>
            ))}
          </div>
        )}

        {/* Tool calls */}
        {message.tool_calls && message.tool_calls.length > 0 && (
          <div className="mt-2 text-xs text-surface-500 px-1">
            <span className="font-medium">Actions taken:</span>
            <ul className="mt-1 space-y-1">
              {message.tool_calls.map((tool, idx) => (
                <li key={idx} className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                  {tool.tool_name || tool.name || tool.tool}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className={`text-xs mt-1.5 px-1 ${isUser ? 'text-right text-brand-200' : 'text-surface-400'}`}>
          {message.created_at && format(new Date(message.created_at), 'h:mm a')}
        </p>
      </div>
    </div>
  )
}

function ToolExecutionIndicator({ executions }) {
  if (executions.length === 0) return null

  return (
    <div className="space-y-2">
      {executions.map((exec, idx) => (
        <div 
          key={idx}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
            exec.status === 'running' 
              ? 'bg-gradient-to-r from-ai-bg to-purple-50 text-ai-700 border border-ai-border dark:from-ai-900/30 dark:to-purple-900/30 dark:text-ai-300 dark:border-ai-700' 
              : 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
          }`}
        >
          <span className="w-4 h-4 shrink-0">
            {exec.status === 'running' ? (
              <svg className="animate-spin" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
            )}
          </span>
          <span className="font-medium">
            {exec.status === 'running' ? `Running: ${exec.tool}...` : `Done: ${exec.tool}`}
          </span>
        </div>
      ))}
    </div>
  )
}
